'use client'

import { MapPin, Search, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import type { SearchSuggestion, Waypoint } from '@/lib/types'
import { cn } from '@/lib/utils'

interface LocationSearchProps {
	label: string
	value: Waypoint | null
	onChange: (w: Waypoint | null) => void
	placeholder?: string
	className?: string
}

export function LocationSearch({
	label,
	value,
	onChange,
	placeholder = 'Search city, address, or venue...',
	className,
}: LocationSearchProps) {
	const [query, setQuery] = useState(value?.name ?? '')
	const [results, setResults] = useState<SearchSuggestion[]>([])
	const [isOpen, setIsOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	// Synchronous re-entrancy guard for the retrieve step. Must be a ref (not
	// state) so a double-Enter fired in the same React tick — before any state
	// flush — still sees the latched value and bails on the second call.
	const retrievingRef = useRef(false)

	// Monotonic sequence guard: each search() call claims the next number and only
	// the most recent claim is allowed to commit results. Out-of-order suggest
	// responses (a slow earlier request resolving after a faster later one) are
	// dropped so stale suggestions can never clobber newer ones.
	const searchSeqRef = useRef(0)

	// Mapbox Search Box bills a "session" = the suggest calls for one typing
	// session + the single retrieve that resolves the pick. Reuse one token
	// across that session, then mint a fresh one after each completed select.
	const sessionRef = useRef<string | null>(null)
	const getSession = useCallback(() => {
		const existing = sessionRef.current
		if (existing) return existing
		const fresh = crypto.randomUUID()
		sessionRef.current = fresh
		return fresh
	}, [])

	// Sync external value changes
	useEffect(() => {
		setQuery(value?.name ?? '')
	}, [value])

	const search = useCallback(
		async (q: string) => {
			// A new query supersedes any prior retrieve failure — clear it so the
			// stale error overlay never lingers over fresh suggestions.
			setError(null)
			if (q.length < 2) {
				setResults([])
				return
			}

			// Claim this request's place in the sequence. Only the latest claim is
			// permitted to mutate state below; earlier in-flight requests are stale.
			const seq = ++searchSeqRef.current

			setIsLoading(true)
			try {
				const res = await fetch(
					`/api/search/suggest?q=${encodeURIComponent(q)}&session=${getSession()}`,
				)
				// A newer search superseded this one while it was in flight — ignore.
				if (seq !== searchSeqRef.current) return
				if (res.ok) {
					const data = await res.json()
					setResults(data.suggestions ?? [])
					setIsOpen(true)
				}
			} catch {
				// Only clear if we are still the latest request; otherwise leave the
				// newer request's results intact.
				if (seq === searchSeqRef.current) setResults([])
			} finally {
				// Guard the loading flag too, so a stale resolution can't prematurely
				// flip off the spinner that a newer in-flight request still owns.
				if (seq === searchSeqRef.current) setIsLoading(false)
			}
		},
		[getSession],
	)

	const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value
		setQuery(val)

		// Clear selection if user modifies text
		if (value) onChange(null)

		// Debounce search
		if (debounceRef.current) clearTimeout(debounceRef.current)
		debounceRef.current = setTimeout(() => search(val), 300)
	}

	/**
	 * Intercept Enter when the autocomplete dropdown is open: pick the first
	 * result rather than letting the form-level Calculate-on-Enter handler fire.
	 * Without this, a user typing "Nashville" would hit Enter to select the
	 * suggestion and accidentally trigger Calculate with no origin set.
	 */
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && isOpen && results.length > 0) {
			const first = results[0]
			if (first) {
				e.preventDefault()
				e.stopPropagation()
				handleSelect(first)
			}
		}
	}

	/**
	 * Resolve a chosen suggestion to coordinates (step 2 — retrieve). Search Box
	 * suggestions carry no lng/lat, so we fetch them here, build the Waypoint,
	 * and start a fresh billing session for the next search.
	 */
	const handleSelect = async (suggestion: SearchSuggestion) => {
		// Re-entrancy guard: a double-Enter (or click-then-Enter) can fire two
		// retrieves in one tick. The synchronous ref latches before any state
		// flush, so the second call returns immediately and we bill one retrieve.
		if (retrievingRef.current) return
		retrievingRef.current = true

		setError(null)
		setIsOpen(false)
		setResults([])
		setIsLoading(true)
		try {
			const res = await fetch(
				`/api/search/retrieve?id=${encodeURIComponent(suggestion.mapbox_id)}&session=${getSession()}`,
			)
			if (!res.ok) {
				// Retrieve failed — keep what they typed and surface a retry hint.
				setError('Could not find that place — please try again.')
				return
			}
			const data = (await res.json()) as {
				name: string
				placeFormatted: string
				center: [number, number]
			}
			const fullName = data.placeFormatted ? `${data.name}, ${data.placeFormatted}` : data.name
			onChange({ name: fullName, lng: data.center[0], lat: data.center[1] })
			setQuery(fullName)
			setError(null)
		} catch {
			// Network error — leave the input untouched and show a retry hint.
			setError('Could not find that place — please try again.')
		} finally {
			setIsLoading(false)
			// Release the re-entrancy latch so the next pick can proceed.
			retrievingRef.current = false
			// New session for the next search (the retrieve closed this one).
			sessionRef.current = null
		}
	}

	const handleClear = () => {
		setQuery('')
		onChange(null)
		setResults([])
		setIsOpen(false)
		setError(null)
		sessionRef.current = null
	}

	// Close dropdown on outside click
	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClick)
		return () => document.removeEventListener('mousedown', handleClick)
	}, [])

	const inputId = `loc-${label.toLowerCase().replace(/\s+/g, '-')}`
	const listboxId = `${inputId}-listbox`

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<label htmlFor={inputId} className="mb-1.5 block text-xs font-medium text-text-secondary">
				{label}
			</label>
			<div className="relative">
				<Search
					className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-text-muted"
					aria-hidden
				/>
				<Input
					id={inputId}
					value={query}
					onChange={handleInput}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					className="pl-9 pr-8"
					onFocus={() => results.length > 0 && setIsOpen(true)}
					// ARIA combobox pattern — screen readers announce this as a combobox
					// with autocomplete suggestions, and the listbox state changes update live
					role="combobox"
					aria-expanded={(isOpen && results.length > 0) || isLoading}
					aria-autocomplete="list"
					aria-controls={listboxId}
				/>
				{(query || value) && (
					<button
						type="button"
						onClick={handleClear}
						aria-label={`Clear ${label.toLowerCase()}`}
						className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-text-muted hover:text-text-primary"
					>
						<X className="size-3.5" aria-hidden />
					</button>
				)}
			</div>

			{isOpen && results.length > 0 && (
				<div
					id={listboxId}
					role="listbox"
					aria-label={`${label} suggestions`}
					className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-surface shadow-lg"
				>
					{results.map((s) => (
						<button
							key={s.mapbox_id}
							type="button"
							role="option"
							aria-selected={false}
							onClick={() => handleSelect(s)}
							className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-surface-hover transition-colors"
						>
							<MapPin className="mt-0.5 size-3.5 shrink-0 text-brand" aria-hidden />
							<span className="min-w-0 flex-1">
								<span className="block truncate text-sm text-text-primary">{s.name}</span>
								{s.place_formatted && (
									<span className="block truncate text-[11px] text-text-muted">
										{s.place_formatted}
									</span>
								)}
							</span>
						</button>
					))}
				</div>
			)}

			{isLoading && (
				<div
					aria-live="polite"
					className="absolute z-50 mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-muted shadow-lg"
				>
					Searching...
				</div>
			)}

			{error && !isLoading && (
				<div
					aria-live="polite"
					className="absolute z-50 mt-1 w-full rounded-md border border-error-fg/60 bg-surface px-3 py-2 text-sm text-error-fg shadow-lg"
				>
					{error}
				</div>
			)}
		</div>
	)
}
